import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "wouter";
import {
  useGetPublicProfile,
  useRecordScan,
  type PublicProfileResponse,
} from "@workspace/api-client-react";
import {
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Globe,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Share2,
  CheckCircle,
  QrCode,
  Eye,
  Copy,
  ArrowRight,
  BadgeCheck,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ProfileTypeLabel } from "../components/profile/ProfileTypeLabel.js";
import { VerifiedBadge } from "../components/profile/VerifiedBadge.js";
import { ContactActionButtons } from "../components/profile/ContactActionButtons.js";
import { ProfileCTA } from "../components/profile/ProfileCTA.js";
import { getThemeById } from "@commonqr/shared/config/themes";
import { identityTypeIdFor } from "@commonqr/shared/config/cardProfileTypes";
import { WhatsAppIcon, XIcon, TikTokIcon, TelegramIcon, SnapchatIcon, BRAND_COLORS } from "@/components/icons/BrandIcons";
import { platformLabel } from "@/lib/platformLabels";
import { getThemeDNA } from "@commonqr/shared/config/themeDNA";
import { resolveTokens, tokensToStyle } from "@/lib/designTokens";
import { profileFromDB } from "@/lib/backgroundAnalyzer";
import { industryFromThemeId } from "@/components/dashboard/ThemeCustomizer";

const PLATFORMS: Record<string, { icon: any; color: string; style: string }> = {
  instagram: {
    icon: Instagram,
    color: "text-white",
    style: "bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600",
  },
  whatsapp: { icon: WhatsAppIcon,  color: "text-white", style: "bg-[#25D366]" },
  facebook: { icon: Facebook,      color: "text-white", style: "bg-[#1877F2]" },
  twitter:  { icon: XIcon,         color: "text-white", style: "bg-black" },
  linkedin: { icon: Linkedin,      color: "text-white", style: "bg-[#0A66C2]" },
  youtube:  { icon: Youtube,       color: "text-white", style: "bg-[#FF0000]" },
  tiktok:   { icon: TikTokIcon,    color: "text-white", style: "bg-black" },
  email:    { icon: Mail,          color: "text-white", style: "bg-blue-500" },
  phone:    { icon: Phone,         color: "text-white", style: "bg-green-600" },
  website:  { icon: Globe,         color: "text-white", style: "bg-slate-800" },
  location: { icon: MapPin,        color: "text-white", style: "bg-red-500" },
  payment:  { icon: CreditCard,    color: "text-white", style: "bg-indigo-500" },
  telegram: { icon: TelegramIcon,  color: "text-white", style: "bg-[#2CA5E0]" },
  snapchat: { icon: SnapchatIcon,  color: "text-black", style: "bg-yellow-400" },
};

const PROFILE_TYPE_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  personal:   { label: "Personal",   bg: "bg-slate-100",  text: "text-slate-600"  },
  consultant: { label: "Consultant", bg: "bg-blue-50",    text: "text-blue-700"   },
  doctor:     { label: "Healthcare", bg: "bg-green-50",   text: "text-green-700"  },
  restaurant: { label: "Restaurant", bg: "bg-orange-50",  text: "text-orange-700" },
  business:   { label: "Business",   bg: "bg-indigo-50",  text: "text-indigo-700" },
  store:      { label: "Store",      bg: "bg-purple-50",  text: "text-purple-700" },
};

const CTA_LABELS: Record<string, string> = {
  personal:   "Connect With Me",
  consultant: "Book Consultation",
  doctor:     "Book Appointment",
  restaurant: "View Menu",
  business:   "Get Quote",
  store:      "Shop Now",
};

// Icons layout: at most 4 icons per row, rows balanced (5 → 3+2, 6 → 3+3, 7 → 4+3), last row centred.
function iconRowWidth(count: number, size: number, gap = 12) {
  const rows = Math.max(1, Math.ceil(count / 4));
  const perRow = Math.ceil(count / rows);
  return perRow * size + (perRow - 1) * gap;
}

const LINKS_VISIBLE_DEFAULT = 7;

interface PublicProfileProps {
  resolvedProfile?: PublicProfileResponse;
  handle?: string;
  recordVisit?: boolean;
  whiteLabel?: boolean;
  pageTitle?: string;
}

export default function PublicProfile({
  resolvedProfile,
  handle,
  recordVisit = true,
  whiteLabel = false,
  pageTitle,
}: PublicProfileProps = {}) {
  const params = useParams<{ username: string }>();
  const username = handle ?? params.username;
  const [copied, setCopied] = useState(false);
  const [showAllLinks, setShowAllLinks] = useState(false);

  const {
    data: fetchedProfile,
    isLoading,
    error,
  } = useGetPublicProfile(username || "", {
    query: {
      queryKey: ["publicProfile", username],
      enabled: !resolvedProfile && !!username,
      retry: false,
      refetchOnWindowFocus: false,
    },
  });
  const profile = resolvedProfile ?? fetchedProfile;

  const recordScan = useRecordScan();

  useEffect(() => {
    if (recordVisit && profile && username) {
      recordScan.mutate({ username });
    }
  }, [profile?.username]);

  const handleShare = () => {
    const url = window.location.href;
    const shareData = { title: displayName, text: `Check out ${displayName} on CommonQR`, url };
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData).catch(() => fallbackCopy(url));
    } else if (navigator.share) {
      navigator.share({ url }).catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
  };

  const copyToClipboard = (url: string) => {
    try {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy(url));
    } catch {
      fallbackCopy(url);
    }
  };

  const fallbackCopy = (url: string) => {
    const el = document.createElement("textarea");
    el.value = url;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();
    try { document.execCommand("copy"); } catch {}
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const _handleShare = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!resolvedProfile && isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if ((!resolvedProfile && error) || !profile) {
    window.location.replace("/404");
    return null;
  }

  const displayName: string = (profile as any).displayName ?? profile.name ?? "";
  const profileUrl  = `https://commonqr.com/${profile.username}`;

  const profileType: string = (profile as any).profileType ?? "personal";
  const identityTypeId = identityTypeIdFor(profileType);
  const currentPlan: string = (profile as any).currentPlan  ?? "free";
  const coverFrom:   string = (profile as any).coverFrom    ?? "#6366f1";
  const coverTo:     string = (profile as any).coverTo      ?? "#9333ea";
  const themeId: string | null = profile.themeId ?? null;
  const themeImageUrl: string | null = profile.themeImageUrl ?? null;
  const phone:       string | undefined = (profile as any).phone   || undefined;
  const email:       string | undefined = (profile as any).email   || profile.email || undefined;
  const website:     string | undefined = (profile as any).website || undefined;

  const isPaid     = currentPlan !== "free";
  const accentColor: string = (profile as any).accentColor ?? "#6366f1";
  const contactGreeting: string | null = (profile as any).contactGreeting ?? null;
  const showBadge  = profile.isVerified && isPaid;
  const typeConfig = PROFILE_TYPE_CONFIG[profileType] ?? { label: "Professional", bg: "bg-slate-100", text: "text-slate-600" };
  const ctaLabel   = CTA_LABELS[profileType] ?? "Connect With Me";
  const phoneDigits = phone?.replace(/\D/g, "") ?? "";

  const allLinks     = profile.links ?? [];
  const visibleLinks = showAllLinks ? allLinks : allLinks.slice(0, LINKS_VISIBLE_DEFAULT);
  const hiddenCount  = allLinks.length - LINKS_VISIBLE_DEFAULT;

  const selectedTheme = getThemeById(themeId);
  const headerImageUrl = themeId ? `/api/v1/themes/image/${themeId}` : null;
  const headerStyle = headerImageUrl
    ? {
        backgroundImage: `url("${headerImageUrl}")`,
        backgroundPosition: "center",
        backgroundSize: "cover",
      }
    : { background: `linear-gradient(to right, ${coverFrom}, ${coverTo})` };

  const savedTheme = profile as any;
  const savedCustomization: any = { uiStyle: savedTheme.themeUiStyle ?? undefined, fontFamily: savedTheme.themeFontFamily ?? undefined, fontSize: savedTheme.themeFontSize ?? undefined, textColor: savedTheme.themeTextColor ?? undefined, buttonColor: savedTheme.themeButtonColor ?? undefined, buttonStyle: savedTheme.themeButtonStyle ?? undefined, linkLayout: savedTheme.themeLinkLayout ?? undefined, iconColor: savedTheme.themeIconColor ?? undefined };
  // Icon colour: "brand" tints each platform icon in its official colour (themed profiles only).
  const iconTint = (platform: string) =>
    headerImageUrl && savedCustomization.iconColor === "brand" && BRAND_COLORS[platform] ? { color: BRAND_COLORS[platform] } : undefined;
  const tokenStyle = headerImageUrl ? tokensToStyle(resolveTokens(getThemeDNA(industryFromThemeId(themeId)), profileFromDB(savedCustomization.buttonColor ?? "#8b5cf6", 0.12, "#ffffff"), savedCustomization)) : undefined;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-start sm:justify-center font-sans py-0 sm:py-8 px-0 sm:px-4">
      <Helmet>
        <title>{pageTitle ?? `${displayName} — CommonQR Profile`}</title>
        <meta name="description" content={profile.bio || `Connect with ${displayName} on CommonQR`} />
        <meta property="og:title"       content={`${displayName} on CommonQR`} />
        <meta property="og:description" content={profile.bio || `Connect with ${displayName}`} />
        <meta property="og:type"        content="profile" />
        <meta property="og:url"         content={profileUrl} />
        <meta property="og:image"       content={`https://commonqr.com/api/v1/og/${profile.username}`} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots"       content="index, follow" />
      </Helmet>

      <div className="w-full max-w-md mx-auto relative sm:rounded-[2.5rem] overflow-hidden flex flex-col border-0 sm:border border-slate-200/60 sm:shadow-2xl min-h-[736px]" style={tokenStyle}>

        {/* ── Full-bleed background ───────────────────────────────── */}
        <div className="absolute inset-0" style={headerStyle}>
          {headerImageUrl ? (
            <div className="absolute inset-0 bg-black/20" style={{ backgroundImage: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 35%, rgba(0,0,0,0) 65%)" }} />
          ) : (
            <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" />
          )}
        </div>

        {/* ── Share button ────────────────────────────────────────── */}
        <div className="absolute top-4 right-4 z-50">
            <button
              onClick={handleShare}
              className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors relative"
            >
              {copied ? <CheckCircle className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
              <AnimatePresence>
                {copied && (
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute -bottom-8 bg-slate-900 text-white text-xs px-2 py-1 rounded-md font-medium whitespace-nowrap"
                  >
                    Copied!
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>

        {/* ── Identity ─────────────────────────────────────────────── */}
        <div className="px-6 pb-5 text-center relative z-10 pt-16">
          {/* Avatar */}
          <div className="w-24 h-24 mx-auto rounded-full border-4 border-white/50 bg-white/20 overflow-hidden shadow-lg flex items-center justify-center shrink-0 backdrop-blur-sm">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-4xl font-display font-bold text-indigo-600 uppercase">
                {(displayName || "?").charAt(0)}
              </span>
            )}
          </div>

          {/* Name */}
          <h1 className={`text-2xl font-extrabold mt-4 flex justify-center items-center gap-1.5 leading-tight ${headerImageUrl ? "text-white" : "text-slate-900"}`}>
            {displayName}
            <VerifiedBadge isVerified={profile.isVerified && isPaid} profileType={identityTypeId} />
          </h1>

          {/* Profile type pill */}
          <div className="flex justify-center mt-2">
            <ProfileTypeLabel profileType={profileType as any} />
          </div>

          {/* Location */}
          {profile.location && (
            <p className={`mt-3 text-sm flex items-center justify-center gap-1 ${headerImageUrl ? "text-white/80" : "text-slate-500"}`}>
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {profile.location}
            </p>
          )}

          {/* Bio */}
          {profile.bio && (
            <p className={`mt-3 leading-relaxed text-sm line-clamp-2 px-2 ${headerImageUrl ? "text-white/80" : "text-slate-600"}`}>
              {profile.bio}
            </p>
          )}

          {/* Website */}
          {website && (
            <a
              href={website.startsWith("http") ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline"
            >
              <Globe className="w-3.5 h-3.5 shrink-0" />
              {website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>

        {/* ── Instant Actions ───────────────────────────────────────── */}
        <div className="px-6 pb-2 relative z-10">
                  <ContactActionButtons
            profile={{ phone: phone ?? null, email: email ?? null, whatsapp: (profile as any).whatsapp ?? null, contactGreeting }}
            profileType={identityTypeId}
          />
        </div>

        {/* ── Primary CTA ──────────────────────────────────────────── */}
        <div className="px-6 pb-4 relative z-10">
            <ProfileCTA
            profileType={identityTypeId}
            accentColor={accentColor}
            profile={{ phone: phone ?? null, email: email ?? null, whatsapp: (profile as any).whatsapp ?? null, website: website ?? null, contactGreeting }}
          />
        </div>

        {/* ── Links ────────────────────────────────────────────────── */}
        {allLinks.length > 0 && (
          <div className="px-6 pb-5">
{savedCustomization?.linkLayout === 'icons' ? (
  <div className="flex flex-wrap justify-center gap-3 relative z-10 mx-auto" style={{ maxWidth: iconRowWidth(visibleLinks.length, 56) }}>
    {visibleLinks.map((link, index) => {
      const platform = PLATFORMS[link.platform] ?? PLATFORMS["website"];
      const Icon = platform.icon;
      return (
        <motion.a
          key={link.id}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.05 }}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          title={link.label || platformLabel(link.platform)}
          className="flex items-center justify-center w-14 h-14 rounded-full transition-transform hover:scale-110"
          style={{ background: "color-mix(in srgb, var(--cqr-color-button, #8b5cf6) calc(var(--cqr-opacity, 0.10) * 100%), transparent)", border: "1px solid var(--cqr-color-border, rgba(255,255,255,0.25))", color: "var(--cqr-color-button-text, #ffffff)", backdropFilter: "blur(var(--cqr-blur, 10px))" } as any}
        >
          <Icon className="w-6 h-6" style={iconTint(link.platform)} />
        </motion.a>
      );
    })}
  </div>
) : savedCustomization?.linkLayout === 'compact' ? (
  <div className="flex flex-col gap-1.5 relative z-10">
    {visibleLinks.map((link, index) => {
      const platform = PLATFORMS[link.platform] ?? PLATFORMS["website"];
      const Icon = platform.icon;
      return (
        <motion.a
          key={link.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 h-9 mx-8 transition-opacity hover:opacity-90"
          style={{ borderRadius: "var(--cqr-radius-button, 9999px)", border: "1px solid var(--cqr-color-border, rgba(255,255,255,0.25))", background: "color-mix(in srgb, var(--cqr-color-button, #8b5cf6) calc(var(--cqr-opacity, 0.10) * 100%), transparent)", color: "var(--cqr-color-button-text, #ffffff)", fontFamily: "var(--cqr-font-button, inherit)", fontSize: "var(--cqr-size-button, 13px)", backdropFilter: "blur(var(--cqr-blur, 10px))" } as any}
        >
          <Icon className="w-4 h-4 shrink-0" style={iconTint(link.platform)} />
          <span>{link.label || platformLabel(link.platform)}</span>
        </motion.a>
      );
    })}
  </div>
) : (
  <div className="flex flex-col gap-3 relative z-10">
    {visibleLinks.map((link, index) => {
      const platform = PLATFORMS[link.platform] ?? PLATFORMS["website"];
      const Icon = platform.icon;
      return (
        <motion.a
          key={link.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          href={link.url}
          style={headerImageUrl ? ({ borderRadius: "var(--cqr-radius-button, 9999px)", border: "1px solid var(--cqr-color-border, rgba(255,255,255,0.25))", background: "color-mix(in srgb, var(--cqr-color-button, #8b5cf6) calc(var(--cqr-opacity, 0.10) * 100%), transparent)", color: "var(--cqr-color-button-text, #ffffff)", fontFamily: "var(--cqr-font-button, inherit)", fontSize: "var(--cqr-size-button, 13px)", fontWeight: "var(--cqr-weight-button, 600)", boxShadow: "var(--cqr-shadow-button, none)", backdropFilter: "blur(var(--cqr-blur, 10px))", WebkitBackdropFilter: "blur(var(--cqr-blur, 10px))" } as any) : undefined}
          target="_blank"
          rel="noopener noreferrer"
          className={headerImageUrl
            ? "flex items-center justify-center gap-3 h-12 w-full px-4 transition-opacity hover:opacity-90"
            : `flex items-center gap-4 p-4 rounded-2xl transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-md border border-slate-100/50 ${platform.style} ${platform.color}`}
        >
          <Icon className={headerImageUrl ? "w-5 h-5 shrink-0" : "w-6 h-6 shrink-0"} style={iconTint(link.platform)} />
          <span className={headerImageUrl ? "truncate" : "font-semibold text-lg"}>{link.label || platformLabel(link.platform)}</span>
        </motion.a>
      );
    })}
  </div>
)}
            {!showAllLinks && hiddenCount > 0 && (
              <button
                onClick={() => setShowAllLinks(true)}
                className="mt-3 w-full py-2.5 rounded-2xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Show {hiddenCount} more
              </button>
            )}
          </div>
        )}

        {/* ── Scan Counter ─────────────────────────────────────────── */}
        <div className="pb-5 flex justify-center relative z-10">
          <span className="bg-slate-50 border border-slate-100 rounded-full px-4 py-1.5 text-sm text-slate-500 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            {(profile as any).scanCount || 0} Profile Views
          </span>
        </div>

        {/* ── Footer ───────────────────────────────────────────────── */}
        {!whiteLabel && <div className="py-6 text-center mt-auto relative z-10 border-t border-white/20">
          <p className="text-xs font-medium text-slate-500 mb-2">
            One QR. Your Permanent Digital Identity. Forever.
          </p>
          <a
            href="/"
            className="text-sm font-bold text-indigo-600 hover:underline inline-flex items-center gap-1"
          >
            Create your free CommonQR profile <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>}
      </div>

    </div>
  );
}